import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AggregatedPortfolioHistory } from '@/lib/api';
import { PORTFOLIO_STALE_TIME_MS } from '@/lib/query-config';

export const HISTORY_QUERY_KEY = 'portfolio-history';

type PortfolioHistoryResponse = {
  portfolioCurrency: string;
  /** Resolution used by the API — useful for labelling chart axes. */
  resolution: 'daily' | 'weekly' | 'monthly';
  history: AggregatedPortfolioHistory[];
};

type PortfolioHistoryFilters = {
  from?: string;
  to?: string;
  type?: string;
  group?: string;
  /**
   * Override automatic resolution selection.
   * When omitted the API auto-selects based on the date range:
   *   ≤ 90 days  → daily
   *   ≤ 365 days → weekly
   *   > 365 days → monthly
   */
  resolution?: 'daily' | 'weekly' | 'monthly';
  /** Filter history to a specific brokerage account. Absent = tenant-wide aggregated view. */
  accountId?: number;
  /** Only this holding: every item with this symbol, summed across accounts unless accountId is set (#131). */
  symbol?: string;
  /** Only this portfolio item — manual / symbol-less assets (#131). Exclusive with symbol. */
  itemId?: number;
};

export function usePortfolioHistory(
  filters: PortfolioHistoryFilters = {},
  { enabled = true }: { enabled?: boolean } = {},
) {
  const query = useQuery<PortfolioHistoryResponse, Error>({
    queryKey: [HISTORY_QUERY_KEY, filters],
    queryFn: () => api.getPortfolioHistory(filters),
    staleTime: PORTFOLIO_STALE_TIME_MS,
    enabled,
  });

  return query;
}
